import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import OTEntryService from '../../services/OTEntryService';
import { toIsoDate } from '../../utils/dateFormat';

// ── Payroll periods ────────────────────────────────────────────────────────
// export const fetchOTEntryPayrollPeriods = createAsyncThunk(
//     'otEntry/fetchPayrollPeriods',
//     async (_, { rejectWithValue }) => {
//         try {
//             const response = await OTEntryService.GetPayrollPeriods();
//             const resultSet =
//                 response?.data?.ResultSet ||
//                 response?.ResultSet ||
//                 response?.data ||
//                 [];
//
//             const list = (Array.isArray(resultSet) ? resultSet : [])
//                 .map((p) => ({
//                     id: String(p.HPP_PAYROLL_PERIOD_ID || ''),
//                     code: p.HPP_PERIOD_CODE || '',
//                     name: p.HPP_PERIOD_NAME || '',
//                     month: p.HPP_MONTH || '',
//                     year: p.HPP_YEAR || '',
//                     status: p.HPP_STATUS || '',
//                     isActive: p.HPP_IS_ACTIVE === 'True',
//                     isLocked: p.HPP_IS_LOCKED === 'True',
//
//                     otStartDate: toIsoDate(p.HPP_OT_START_DATE),
//                     otEndDate: toIsoDate(p.HPP_OT_END_DATE),
//                     attendanceStart: toIsoDate(p.HPP_ATTENDANCE_START_DATE),
//                     attendanceEnd: toIsoDate(p.HPP_ATTENDANCE_END_DATE),
//                     payrollStart: toIsoDate(p.HPP_PAYROLL_START_DATE),
//                     payrollEnd: toIsoDate(p.HPP_PAYROLL_END_DATE),
//                     payDate: toIsoDate(p.HPP_PAYMENT_DATE),
//                 }))
//                 .filter((p) => p.id)
//                 .sort((a, b) => Number(b.id) - Number(a.id));
//
//             return list;
//         } catch (err) {
//             return rejectWithValue(err.message || 'Failed to load payroll periods.');
//         }
//     }
// );

// ── Add ────────────────────────────────────────────────────────────────────
export const addOvertime = createAsyncThunk(
    'otEntry/addOvertime',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await OTEntryService.AddOvertime(payload);

            const statusCode =
                response?.data?.StatusCode ?? response?.StatusCode;
            if (statusCode !== undefined && statusCode !== 200) {
                throw new Error(
                    response?.data?.Message ||
                    response?.data?.Result ||
                    `Failed to add overtime (status ${statusCode}).`
                );
            }
            return {
                mode: 'add',
                payload,
                message:
                    response?.data?.Result ||
                    response?.data?.Message ||
                    'Overtime added successfully.',
            };
        } catch (err) {
            return rejectWithValue(err.message || 'Failed to add overtime.');
        }
    }
);

// ── Update ─────────────────────────────────────────────────────────────────
export const updateOvertime = createAsyncThunk(
    'otEntry/updateOvertime',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await OTEntryService.UpdateOvertime(payload);

            const statusCode =
                response?.data?.StatusCode ?? response?.StatusCode;
            if (statusCode !== undefined && statusCode !== 200) {
                throw new Error(
                    response?.data?.Message ||
                    response?.data?.Result ||
                    `Failed to update overtime (status ${statusCode}).`
                );
            }
            return {
                mode: 'update',
                payload,
                message:
                    response?.data?.Result ||
                    response?.data?.Message ||
                    'Overtime updated successfully.',
            };
        } catch (err) {
            return rejectWithValue(err.message || 'Failed to update overtime.');
        }
    }
);

// ── Load by id (for edit mode) ─────────────────────────────────────────────
export const fetchOvertimeById = createAsyncThunk(
    'otEntry/fetchOvertimeById',
    async (overtimeId, { rejectWithValue }) => {
        try {
            const response = await OTEntryService.GetOvertimeById(overtimeId);
            const raw =
                response?.data?.ResultSet?.[0] ||
                response?.ResultSet?.[0] ||
                response?.data?.Result ||
                null;
            return raw;
        } catch (err) {
            return rejectWithValue(err.message || 'Failed to load overtime.');
        }
    }
);

// ── List (for the Update tab) ──────────────────────────────────────────────
export const fetchOvertimeList = createAsyncThunk(
    'otEntry/fetchOvertimeList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await OTEntryService.GetOvertimeList();
            const resultSet =
                response?.data?.ResultSet || response?.ResultSet || response?.data || [];

            const list = (Array.isArray(resultSet) ? resultSet : []).map((r) => ({
                id: String(r.HOT_OVERTIME_ID || ''),
                employeeId: String(r.HOT_EMPLOYEE_ID || ''),
                payrollPeriodId: String(r.HOT_PAYROLL_PERIOD_ID || ''),
                otDate: toIsoDate(r.HOT_OT_DATE),
                otType: r.HOT_OT_TYPE || 'T',
                startTime: (r.HOT_START_TIME || '').slice(0, 5),
                endTime: (r.HOT_END_TIME || '').slice(0, 5),
                hours: Number(r.HOT_TOTAL_HOURS || 0),
                rate: Number(r.HOT_OT_RATE || 0),
                amount: Number(r.HOT_OT_AMOUNT || 0),
                isApproved: r.HOT_IS_APPROVED === 'True' || r.HOT_IS_APPROVED === true,
                isActive: r.HOT_IS_ACTIVE === 'True' || r.HOT_IS_ACTIVE === true,
                raw: r,
            }));

            return list;
        } catch (err) {
            return rejectWithValue(err.message || 'Failed to load overtime records.');
        }
    }
);

// ── Initial state ──────────────────────────────────────────────────────────
const initialState = {
    // payrollPeriods: [],
    // periodsLoading: false,
    // periodsError: null,

    overtimeList: [],
    listLoading: false,
    listError: null,

    submitting: false,
    loadingById: false,
    successMessage: null,
    error: null,
    lastSaved: null,
};

// ── Slice ──────────────────────────────────────────────────────────────────
const overtimeEntrySlice = createSlice({
    name: 'overtimeEntry',
    initialState,
    reducers: {
        clearOTEntryStatus(state) {
            state.error = null;
            state.successMessage = null;
            state.lastSaved = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ── Periods ──
            // .addCase(fetchOTEntryPayrollPeriods.pending, (state) => {
            //     state.periodsLoading = true;
            //     state.periodsError = null;
            // })
            // .addCase(fetchOTEntryPayrollPeriods.fulfilled, (state, action) => {
            //     state.periodsLoading = false;
            //     state.payrollPeriods = action.payload;
            // })
            // .addCase(fetchOTEntryPayrollPeriods.rejected, (state, action) => {
            //     state.periodsLoading = false;
            //     state.periodsError = action.payload || 'Failed to load payroll periods.';
            // })

            // ── Add ──
            .addCase(addOvertime.pending, (state) => {
                state.submitting = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(addOvertime.fulfilled, (state, action) => {
                state.submitting = false;
                state.successMessage = action.payload.message;
                state.lastSaved = action.payload;
            })
            .addCase(addOvertime.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload || 'Failed to add overtime.';
            })

            // ── Update ──
            .addCase(updateOvertime.pending, (state) => {
                state.submitting = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(updateOvertime.fulfilled, (state, action) => {
                state.submitting = false;
                state.successMessage = action.payload.message;
                state.lastSaved = action.payload;

                const p = action.payload.payload;
                state.overtimeList = state.overtimeList.map((r) =>
                    r.id === String(p.overtimeId)
                        ? {
                            ...r,
                            otDate: p.otDate,
                            otType: p.otType,
                            startTime: p.startTime,
                            endTime: p.endTime,
                            hours: p.totalHours,
                            rate: p.otRate,
                            amount: p.otAmount,
                            payrollPeriodId: String(p.payrollPeriodId),
                        }
                        : r
                );
            })
            .addCase(updateOvertime.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload || 'Failed to update overtime.';
            })

            // ── Load by id ──
            .addCase(fetchOvertimeById.pending, (state) => {
                state.loadingById = true;
                state.error = null;
            })
            .addCase(fetchOvertimeById.fulfilled, (state) => {
                state.loadingById = false;
            })
            .addCase(fetchOvertimeById.rejected, (state, action) => {
                state.loadingById = false;
                state.error = action.payload || 'Failed to load overtime.';
            })
            // ── List (Update tab) ──
            .addCase(fetchOvertimeList.pending, (state) => {
                state.listLoading = true;
                state.listError = null;
            })
            .addCase(fetchOvertimeList.fulfilled, (state, action) => {
                state.listLoading = false;
                state.overtimeList = action.payload;
            })
            .addCase(fetchOvertimeList.rejected, (state, action) => {
                state.listLoading = false;
                state.listError = action.payload || 'Failed to load overtime records.';
            })
    },
});

export const { clearOTEntryStatus } = overtimeEntrySlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────
// export const selectOTEntryPeriods = (s) => s.overtimeEntry?.payrollPeriods || [];
// export const selectOTEntryPeriodsLoading = (s) => s.overtimeEntry?.periodsLoading || false;
export const selectOTEntrySubmitting = (s) => s.overtimeEntry?.submitting || false;
export const selectOTEntryError = (s) => s.overtimeEntry?.error || null;
export const selectOTEntrySuccess = (s) => s.overtimeEntry?.successMessage || null;
export const selectOTEntryList = (s) => s.overtimeEntry?.overtimeList || [];
export const selectOTEntryListLoading = (s) => s.overtimeEntry?.listLoading || false;
export const selectEditableOTList = (s) =>
    (s.overtimeEntry?.overtimeList || []).filter(
        (r) => !r.isApproved && r.isActive
    );

export default overtimeEntrySlice.reducer;